import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useServices } from "../../context/ServiceProvider";
import { Alert, Badge, Button, Table, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { ChevronDown, ChevronRight } from "lucide-react";
import CreateQualificationStepModal from "../project-qual-step/CreateQualificationStepModal";

/**
 * Reusable section component for displaying Qualification Steps
 * @param {string} projectId - The project ID to fetch qual steps for (optional)
 * @param {string} projectObjectiveId - The project objective ID to fetch qual steps for (optional)
 */
const ProjectQualStepsSection = ({
  projectId,
  projectName,
  projectObjectiveId,
}) => {
  const { projectQualStep } = useServices();
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState("");
  const [list, setList] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);

  async function load() {
    if (!projectId && !projectObjectiveId) return;
    setLoading(true);
    setError("");
    try {
      let data;
      if (projectObjectiveId) {
        data =
          await projectQualStep.fetchQualStepsByProjectObjectiveId(
            projectObjectiveId,
          );
      } else if (projectId) {
        data = await projectQualStep.fetchQualStepsByProjectId(projectId);
      }
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || "Failed to load qualification steps");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, projectObjectiveId]);

  const handleAddSuccess = () => {
    setShowAddModal(false);
    load(); // Reload the list after successful creation
  };

  const description = projectObjectiveId
    ? "List of qualification steps for this objective"
    : "List of qualification steps for this project";

  return (
    <>
      <Badge.Ribbon
        placement="start"
        style={{ display: loading === false ? "block" : "none" }}
        text={`#${list.length}`}
        color="blue"
      >
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between p-6">
            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              className="flex-1 flex items-start flex-col text-left"
            >
              <div className="text-lg font-semibold text-gray-900">
                Qualification Steps
              </div>
              <div className="text-sm text-gray-600">{description}</div>
            </button>
            <div className="flex items-center gap-2">
              <Button
                type="primary"
                onClick={() => setShowAddModal(true)}
                size="small"
                icon={<PlusOutlined />}
              >
                Add
              </Button>
              <button
                type="button"
                onClick={() => setIsExpanded((v) => !v)}
                className="p-1"
              >
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
              </button>
            </div>
          </div>
          {isExpanded && (
            <div className="border-t border-gray-200 p-4">
              {error && (
                <div className="mb-2">
                  <Alert type="error" showIcon message={error} />
                </div>
              )}
              <Table
                loading={loading}
                dataSource={list}
                rowKey="Id"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "Name",
                    dataIndex: "Name",
                    key: "Name",
                    width: "40%",
                    ellipsis: true,
                    render: (text, rec) => (
                      <Link
                        to={{ pathname: `/pqs/${rec.Id}` }}
                        state={{ crumbLabel: rec.Name }}
                        className="text-primary-600"
                      >
                        {text || rec.Id}
                      </Link>
                    ),
                  },
                  {
                    title: "Project Objective",
                    dataIndex: ["Project_Objective__r", "Name"],
                    key: "Project_Objective",
                    width: "30%",
                    ellipsis: true,
                    render: (text, rec) => {
                      const po = rec.Project_Objective__r;
                      if (!po) return "-";
                      return (
                        <Link
                          to={{ pathname: `/po/${po.Id}` }}
                          state={{ crumbLabel: po.Name }}
                          className="text-primary-600"
                        >
                          {text || po.Name}
                        </Link>
                      );
                    },
                  },
                  {
                    title: "Qualification Step",
                    dataIndex: ["Qualification_Step__r", "Name"],
                    key: "Qualification_Step",
                    width: "30%",
                    ellipsis: true,
                    render: (text) => {
                      return <Typography.Text>{text}</Typography.Text>;
                    },
                  },
                ]}
              />
            </div>
          )}
        </div>
      </Badge.Ribbon>
      <CreateQualificationStepModal
        visible={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAddSuccess}
        initialValues={{ projectId, projectName }}
      />
    </>
  );
};

export default ProjectQualStepsSection;
